import { authenticatedRestClient, withCookiesRestClient } from 'helpers/restclient';
import { getRailsBaseUrl, publicEnvConfig } from 'helpers/env';

export default class UserSessionService {
	static instance = new UserSessionService();

	_endpoints = {
		logout: '/api/users/sessions',
		groupAccounts: '/api/sellers/masters/users',
		changeAccount: '/api/masters/sessions',
		reloadUser: '/users/cookie',
		getUserNavigationInfo: '/api/users/navigation',
		acknowledgeCreditAnnouncement: (customerSelfServeCreditId) =>
			`/api/users/credit_announcements/${customerSelfServeCreditId}/acknowledge`,
		signIn: '/api/users/sessions',
		refreshAccessToken: '/api/users/sessions/refresh',
		oktaProfiles: '/api/okta_profiles',
		oktaLinkPostReset: (token) => `/api/okta_link_post_reset/${encodeURIComponent(token)}`,
		oktaSendTokenEmail: `api/okta_profiles/send_access_email`,
		oktaLoginFailTracking: (email) => `/api/okta_users/${encodeURIComponent(email)}/unsuccessful_login`,
	};

	constructor() {
		this.authenticatedRestClient = authenticatedRestClient;
		this.withCookiesRestClient = withCookiesRestClient;
	}

	logout = () => this.authenticatedRestClient.delete(`${this._endpoints.logout}`, {});

	getGroupAccounts = async () => await this.authenticatedRestClient.get(this._endpoints.groupAccounts, {});

	changeAccount = async (id) => {
		const { data } = await this.authenticatedRestClient.post(
			`${publicEnvConfig.RELATIVE_BASE_URL}${this._endpoints.changeAccount}`,
			{ user_id: id },
		);
		return data?.redirect_path;
	};

	reloadUser = async () => {
		await this.authenticatedRestClient.post(
			`${publicEnvConfig.RELATIVE_BASE_URL}${this._endpoints.reloadUser}`,
			{},
		);
	};

	getUserNavigationInfo = async (accessToken) =>
		await this.withCookiesRestClient.get(
			`${getRailsBaseUrl()}${this._endpoints.getUserNavigationInfo}`,
			undefined,
			// WHY we need this header only in this service?
			{
				Authorization: `Token token=${accessToken}`,
			},
		);

	acknowledgeCreditAnnouncement = (customerSelfServeCreditId) =>
		this.authenticatedRestClient.patch(
			this._endpoints.acknowledgeCreditAnnouncement(customerSelfServeCreditId),
			{},
		);

	signIn = (email, password, utmParams, oktaCode) => {
		const requestBody = oktaCode ? {} : { user: { email, password } };

		const requestHeaders = oktaCode ? { 'Okta-Access-Token': oktaCode } : {};

		const oktaV2Header = Boolean(oktaCode);
		if (oktaV2Header) requestHeaders['Okta-Version'] = '2.0';

		const queryParams = utmParams ? `&${utmParams}` : '';
		return this.withCookiesRestClient.post(`${this._endpoints.signIn}?${queryParams}`, requestBody, requestHeaders);
	};

	// this is ment to be used on server side
	refreshAccessToken = (refreshToken, headers) => {
		return this.withCookiesRestClient.post(
			`${getRailsBaseUrl()}${this._endpoints.refreshAccessToken}`,
			{},
			{
				Cookie: `;refresh_token=${refreshToken}`,
				...headers,
			},
		);
	};

	refreshAccessTokenClientSide = async () => {
		return this.withCookiesRestClient.post(this._endpoints.refreshAccessToken, {});
	};

	checkOktaProfile = ({ username, password }) => {
		return this.withCookiesRestClient.post(
			// send email via query param just for logging purposes.
			`${getRailsBaseUrl()}${this._endpoints.oktaProfiles}?email=${encodeURIComponent(username)}`,
			{
				username,
				password,
			},
		);
	};

	confirmOktaProfileLink = ({ invitationToken }) => {
		return this.withCookiesRestClient.put(
			`${getRailsBaseUrl()}${this._endpoints.oktaProfiles}/${invitationToken}`,
			{},
		);
	};

	oktaLinkPostReset = ({ resetPasswordToken }) => {
		return this.withCookiesRestClient.post(
			`${getRailsBaseUrl()}${this._endpoints.oktaLinkPostReset(resetPasswordToken)}`,
			{},
		);
	};

	sendOktaTokenEmail = ({ email }) => {
		return this.withCookiesRestClient.post(this._endpoints.oktaSendTokenEmail, { email });
	};

	oktaLoginFailTracking = ({ email }) => {
		return this.withCookiesRestClient.post(
			`${getRailsBaseUrl()}${this._endpoints.oktaLoginFailTracking(email)}`,
			{},
		);
	};
}
